import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createOrReuseIdentityVerificationSession } from "../api/identity.api";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import Card from "../components/shared/card";
import Typography from "../components/shared/typography";
import { IdentityVerifyGateProvider } from "../hooks/IdentityVerifyContext";
import { useIdentityVerificationStatus } from "../hooks/useIdentityStatus";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as
  | string
  | undefined;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : Promise.resolve(null);

export default function IdentityVerifyPage() {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [status, setStatus] = useState<
    "idle" | "loading" | "ready" | "verifying" | "completed" | "failed"
  >("idle");
  const [error, setError] = useState<string | null>(null);

  const [isPending, setIsPending] = useState(false);
  const { data: serverStatus, isFetching: serverFetching } =
    useIdentityVerificationStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const continuedRef = useRef(false);

  const redirectTarget = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("redirect") || "/";
  }, [location.search]);

  const updateDraftToSignStep = useCallback(() => {
    try {
      // Try to extract slug from redirect path /request-records/:slug
      let slug: string | undefined;
      try {
        const url = new URL(redirectTarget, window.location.origin);
        const parts = url.pathname.split("/").filter(Boolean);
        if (parts[0] === "request-records" && parts[1])
          slug = decodeURIComponent(parts[1]);
      } catch {
        const pathname = redirectTarget.split("?")[0] || "";
        const parts = pathname.split("/").filter(Boolean);
        if (parts[0] === "request-records" && parts[1])
          slug = decodeURIComponent(parts[1]);
      }
      if (!slug) return;
      const storageKey = `request-records:${slug}`;
      const raw = localStorage.getItem(storageKey);
      const existing = raw ? JSON.parse(raw) : {};
      const form = existing.form ?? existing ?? {};
      const step = typeof existing.step === "number" ? existing.step : 0;
      const statusObj = existing.status ?? {};
      const next = {
        form,
        step,
        status: {
          ...statusObj,
          identityVerified: true,
          identityVerifiedTimestamp: new Date().toISOString(),
          identityVerificationType: "license",
          phase: 3,
          signatureCompleted: Boolean(statusObj.signatureCompleted),
        },
      };
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch (e) {
      // best-effort only
    }
  }, [redirectTarget]);

  const runVerification = useCallback(async () => {
    if (!clientSecret) return;
    try {
      setStatus("verifying");
      if (!STRIPE_PK) {
        setError("Missing Stripe publishable key.");
        setStatus("failed");
        return;
      }
      const stripe = await stripePromise;
      if (!stripe) {
        setError("Stripe failed to initialize.");
        setStatus("failed");
        return;
      }
      const result = await stripe.verifyIdentity(clientSecret);
      if (result?.error) {
        setError(result.error.message || "Verification failed");
        setStatus("failed");
      } else {
        setStatus("completed");
      }
    } catch (e: any) {
      setError(e?.message || "Unexpected error during verification");
      setStatus("failed");
    }
  }, [clientSecret]);

  // Once verification completes, push user back to the sign step
  useEffect(() => {
    if (status === "completed" && !continuedRef.current) {
      continuedRef.current = true;
      updateDraftToSignStep();
      navigate(redirectTarget, { replace: true });
    }
  }, [status, navigate, redirectTarget, updateDraftToSignStep]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setStatus("loading");
      setError(null);
      try {
        setIsPending(true);
        const { client_secret } =
          await createOrReuseIdentityVerificationSession();
        if (cancelled) return;
        setClientSecret(client_secret);
        setStatus("ready");
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || "Failed to create verification session");
        setStatus("failed");
      } finally {
        setIsPending(false);
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Auto-start once ready
    if (status === "ready" && clientSecret) {
      runVerification();
    }
  }, [status, clientSecret, runVerification]);

  return (
    <div className="min-h-screen container mx-auto px-4 py-8">
      <IdentityVerifyGateProvider>
        <Card className="w-full max-w-md mx-auto" variant="cream" elevation={2}>
          <Card.Header variant="cream" className="text-center">
            <Typography variant="h3" className="text-primary mb-2">
              Identity Verification
            </Typography>
            <Typography variant="body2" className="text-gray-600">
              Securely verify your identity with Stripe.
            </Typography>
            <Typography variant="body2" className="text-brown-700 mt-1">
              This process requires a valid photo ID.
            </Typography>
            <Typography variant="body2" className="text-terracotta-600 mt-2">
              Note: Stripe opens a new window. Please disable pop-up blockers or
              allow pop-ups for this site.
            </Typography>
          </Card.Header>
          <Card.Body>
            <div className="mb-4">
              <Typography variant="body2" className="text-gray-700">
                Server status: {serverStatus?.status ?? "unknown"}
                {serverFetching ? " (updating...)" : ""}
              </Typography>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
                <Typography variant="body2">{error}</Typography>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Typography variant="body2" className="text-gray-700">
                  Local flow: {status}
                </Typography>
              </div>

              <SubmitButton
                type="button"
                isDisabled={isPending || status === "verifying"}
                isLoading={isPending || status === "verifying"}
                onClick={runVerification as any}
                className="w-full"
              >
                {status === "completed" ? "Verified" : "Start Verification"}
              </SubmitButton>
            </div>
          </Card.Body>
        </Card>
      </IdentityVerifyGateProvider>
    </div>
  );
}
